/**
 * Web HTTP Adapter
 * Implements the HttpAdapter interface using the native browser fetch API
 */

import type { HttpAdapter, HttpRequest, HttpResponse } from 'rainowls-shared'

export class WebHttpAdapter implements HttpAdapter {
  async request(options: HttpRequest): Promise<HttpResponse> {
    const controller = new AbortController()
    const timeoutId = setTimeout(
      () => controller.abort(),
      options.timeout || 30000
    )

    try {
      const response = await fetch(options.url, {
        method: options.method,
        headers: options.headers,
        signal: controller.signal,
      })

      // JSON APIs also use structured media types such as application/geo+json.
      let content: unknown
      const contentType = response.headers.get('content-type')?.split(';')[0]?.trim().toLowerCase()

      if (contentType === 'application/json' || contentType?.endsWith('+json')) {
        content = await response.json()
      } else {
        content = await response.text()
      }

      return {
        statusCode: response.status,
        content,
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`Request timeout after ${options.timeout || 30000}ms`)
      }

      throw error
    } finally {
      // Keep the deadline active until the response body has been consumed.
      clearTimeout(timeoutId)
    }
  }
}
